/**
 * Array utility functions for chunking, batching, and collection operations.
 */

/**
 * Splits an array into chunks of a specified size.
 *
 * @param arr - The array to chunk
 * @param size - The maximum size of each chunk
 * @returns Array of chunks
 *
 * @example
 * chunk([1, 2, 3, 4, 5], 2) // [[1, 2], [3, 4], [5]]
 */
export function chunk<T>(arr: readonly T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, i * size + size));
}
/**
 * Splits an array into two buckets based on a predicate, in a single pass.
 * Returns `[matching, nonMatching]`.
 *
 * @example
 * partition([1, 2, 3, 4], (n) => n % 2 === 0) // [[2, 4], [1, 3]]
 */
export function partition<T>(items: readonly T[], predicate: (item: T) => boolean): [T[], T[]] {
  return items.reduce<[T[], T[]]>(
    (acc, item) => {
      acc[predicate(item) ? 0 : 1].push(item);
      return acc;
    },
    [[], []],
  );
}

/**
 * Generator that yields chunks of an array lazily.
 * Useful for processing large arrays without creating intermediate arrays.
 *
 * @param arr - The array to chunk
 * @param size - The maximum size of each chunk
 * @yields Chunks of the array
 *
 * @example
 * for (const batch of chunks([1, 2, 3, 4, 5], 2)) {
 *   console.log(batch); // [1, 2], [3, 4], [5]
 * }
 */
export function* chunks<T>(arr: readonly T[], size: number): Generator<T[], void> {
  for (let i = 0; i < arr.length; i += size) {
    yield arr.slice(i, i + size);
  }
}

/**
 * Find item in array and update it, or create new if not found.
 * Returns new array (immutable).
 *
 * @param items - Source array
 * @param predicate - Function to find existing item
 * @param update - Function to update existing item (receives existing item)
 * @param create - Function to create new item if not found
 * @returns New array with item updated or added
 *
 * @example
 * ```typescript
 * const nodes = findOrUpdate(
 *   existingNodes,
 *   (n) => n.id === newData.id,
 *   (existing) => ({ ...existing, ...newData }),
 *   () => ({ id: newData.id, ...newData })
 * );
 * ```
 */
export function findOrUpdate<T>(
  items: readonly T[],
  predicate: (item: T) => boolean,
  update: (existing: T) => T,
  create: () => T,
): T[] {
  const index = items.findIndex(predicate);
  if (index >= 0) {
    return items.map((item, i) => (i === index ? update(item) : item));
  }
  return [...items, create()];
}
